import React, { useState } from 'react';
import { generateTeaCeremony } from '../services/geminiService';
import { TeaCeremonyGuide } from '../types';
import { Coffee, ArrowRight, RefreshCw, Sparkles, Wind } from 'lucide-react';
import { useUser } from '../context/UserContext';

const IchigoTeaRoom: React.FC = () => {
  const [context, setContext] = useState('');
  const [guide, setGuide] = useState<TeaCeremonyGuide | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleGenerate = async () => {
    if (!context.trim()) return;
    setLoading(true);
    setGuide(null);
    try {
      const data = await generateTeaCeremony(context);
      setGuide(data);
      addXp(30, 'Prepared in Tea Room');
      unlockBadge('tea_master');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-amber-50 text-amber-600 rounded-full mb-4">
          <Coffee size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Tea Room</h1>
        <p className="text-nunchi-600">
          Prepare your mind for an upcoming interaction. <br/>
          Whether it's a meeting, a date, or a quiet coffee alone, make it a ceremony.
        </p>
      </header>

      {!guide && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What interaction are you preparing for?
            </label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g., Having lunch with my mom, Discussing a raise with boss, Drinking tea on the porch..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-amber-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleGenerate}
              disabled={!context.trim()}
              className="w-full bg-amber-700 text-white py-4 rounded-xl font-bold hover:bg-amber-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Sparkles size={18} /> Design My Ceremony
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-amber-100 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-amber-700">Brewing your mindset...</p>
        </div>
      )}

      {guide && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 relative overflow-hidden shadow-sm">
                 <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-200 to-rose-200"></div>
                 
                 <div className="text-center mb-8">
                     <span className="inline-block px-3 py-1 bg-amber-50 text-amber-600 text-xs font-bold uppercase tracking-widest rounded-full mb-3">
                         Theme
                     </span>
                     <h2 className="text-4xl font-serif font-bold text-nunchi-900">{guide.theme}</h2>
                 </div>

                 <div className="mb-8 p-6 bg-nunchi-50 rounded-xl">
                     <h3 className="flex items-center gap-2 font-bold text-nunchi-700 mb-2">
                         <Wind size={18} /> Atmosphere
                     </h3>
                     <p className="text-nunchi-600 leading-relaxed italic font-serif">
                         "{guide.atmosphere}"
                     </p>
                 </div>

                 <div className="space-y-4 mb-8">
                     <h3 className="font-bold text-nunchi-800 uppercase text-sm tracking-wider">Preparation Steps</h3>
                     {guide.steps.map((step, idx) => (
                         <div key={idx} className="flex gap-4 items-start">
                             <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                                 {idx + 1}
                             </div>
                             <p className="text-nunchi-700">{step}</p>
                         </div>
                     ))}
                 </div>

                 <div className="text-center border-t border-nunchi-100 pt-6">
                     <p className="text-xs text-nunchi-400 uppercase tracking-widest mb-2">Your Mantra</p>
                     <p className="text-lg font-bold text-nunchi-800">"{guide.mindset}"</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setGuide(null); setContext(''); }}
                  className="text-nunchi-500 hover:text-nunchi-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Prepare Another Moment
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default IchigoTeaRoom;